import { Box, Button, FloatingNotification, Heading, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { useRouter } from 'next/router';
import { FunctionComponent, useContext, useEffect } from 'react';
import { BI, EXPERIMENTS, LOCALES } from '../../../constants';
import { Context } from '../../../context';
import { DATA_HOOKS } from '../../../dataHooks';
import { useBI } from '../../../hooks/useBI';
import { ArticleSearchResult } from '../../../types';
import { isMobile } from '../../../utils/userAgent';
import { Breadcrumbs } from '../../Breadcrumbs';
import css from './index.module.scss';
import { gotNoAnswer } from '@wix/bi-logger-new-help-center/v2';
import { useExperiments } from '@wix/fe-essentials-standalone';
import Image from 'next/image';
import { Hint } from 'wix-ui-icons-common';

export type NoResultsFoundProps = {
  term: string;
  data?: Pick<ArticleSearchResult, 'searchMethod' | 'searchVersion'> | null;
};

export const NoResultsFound: FunctionComponent<NoResultsFoundProps> = ({
  term,
  data,
}) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const context = useContext(Context);
  const { searchSessionId } = context;
  const { sendBIEvent } = useBI();
  const { experiments } = useExperiments({ readOnly: true });
  const { setShowSearchModal } = useContext(Context);

  const newEmptyStateEnabled = experiments.enabled(EXPERIMENTS.SPECS.NEW_SEARCH_EMPTY_STATE) && locale === LOCALES.EN;

  const breadcrumbItems = [
    { id: 'search-results', label: t('search-results-page.breadcrumb') },
  ];

  useEffect(() => {
    void (async () => {
      if (searchSessionId) {
        await sendBIEvent(
          gotNoAnswer({
            kb_lang: locale as string,
            source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
            search_method: data?.searchMethod,
            search_version: newEmptyStateEnabled ? 'new' : data?.searchVersion,
            question: term,
            search_session_id: searchSessionId,
          })
        );
      }
    })();
  }, [data, locale, term, searchSessionId]);

  const deprecatedEmptyStateComp = (
    <Box
      className={css.NoResultsFound}
      direction="vertical"
      verticalAlign="middle"
      align="center"
      dataHook={DATA_HOOKS.SEARCH_RESULTS_NO_RESULTS_FOUND}
    >
      <Heading
        appearance="H2"
        dataHook={DATA_HOOKS.SEARCH_RESULTS_NO_RESULTS_FOUND_TITLE}
        className={css.title}
      >
        {t('search-results-page.no-results-found.title', { term })}
      </Heading>
      <Text
        weight="thin"
        size="medium"
        dataHook={DATA_HOOKS.SEARCH_RESULTS_NO_RESULTS_FOUND_DESCRIPTION}
        className={css.description}
      >
        {t('search-results-page.no-results-found.description')}
      </Text>
      <Button
        size={isMobile(context) ? 'small' : 'medium'}
        as="a"
        href={`/${locale}`}
        skin="standard"
        dataHook={DATA_HOOKS.SEARCH_RESULTS_NO_RESULTS_FOUND_CTA}
      >
        {t('search-results-page.no-results-found.return-to-homepage')}
      </Button>
    </Box>
  );

  const emptyStateComp = (
    <Box
      className={css.noResultsFoundNew}
      direction="vertical"
      verticalAlign="middle"
      align="center"
      dataHook={DATA_HOOKS.SEARCH_RESULTS_NO_RESULTS_FOUND}
    >
      <Image alt='empty search illustration' src='https://static.parastorage.com/services/kb-client-nextjs/dist/public/images/search-results/empty-search.svg' height='120' width='120' />
      <Heading
        as='h2'
        dataHook={DATA_HOOKS.SEARCH_RESULTS_NO_RESULTS_FOUND_TITLE}
        className={css.newTitle}
      >
        {t('search-results-page.no-results-found.new.title', { term })}
      </Heading>
      <Button
        dataHook={DATA_HOOKS.SEARCH_RESULTS_NO_RESULTS_FOUND_CTA}
        size='medium'
        onClick={() => setShowSearchModal(true)}
        className={css.newCta}
      >
        {t('search-results-page.no-results-found.new.cta')}
      </Button>
      <FloatingNotification
        dataHook={DATA_HOOKS.SEARCH_RESULTS_NO_RESULTS_FOUND_FLOATING}
        showCloseButton={false}
        className={css.floatingNotification}
        prefixIcon={<Hint/>}
        text={
          <Text
            size='small'
          >
            {t('search-results-page.no-results-found.new.floating-notification')}
          </Text>
        }
      />
    </Box>
  );

  return (
    <>
      <Breadcrumbs breadcrumbItems={breadcrumbItems} shallow={false} />
      {newEmptyStateEnabled ? emptyStateComp : deprecatedEmptyStateComp}
    </>
  );
};
